import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@mantine/core";
import { ReactComponent as Logo } from "@/assets/dinamo-logo.svg";
import { ReactComponent as GridViewIcon } from "@/assets/icons/view/grid.svg";
import { ReactComponent as ListViewIcon } from "@/assets/icons/view/list.svg";
import { ReactComponent as SingleViewIcon } from "@/assets/icons/view/single.svg";
import SingleView from "@/components/User/Shared/SingleView/SingleView";
import GridView from "@/components/User/Shared/GridView/GridView";
import ListView from "@/components/User/Shared/ListView/ListView";
import { Publication } from "@/api/types";
import { PublicationApi } from "@/api/publication.api";
import { OrderApi } from "@/api/order.api";
import clsx from "clsx";
import "./Publications.css";

const Publications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get("view") || "list";
  const [activeView, setView] = useState(initialView);
  const isSingleView = activeView === "single";
  const isListView = activeView === "list";
  const isGridView = activeView === "grid";

  useEffect(() => {
    setSearchParams({ view: activeView });
  }, [activeView, setSearchParams]);

  const [publications, setPublications] = useState<Publication[]>([]);
  const [flipbookOpened, setFlipbookOpened] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState("");
  const [orderedPublications, setOrderedPublications] =
    useState<Publication[]>();
  const [myPublications, setMyPublications] = useState<Publication[]>([]);
  let flipbookContainer: HTMLElement | null = null;

  const openFlipbook = (pdfUrl: string) => {
    setSelectedPdf(pdfUrl);
    setFlipbookOpened(true);
  };

  const getPublications = async () => {
    const result = await PublicationApi.getPublications();
    if (result) setPublications(result);
  };

  const getMyPublications = async () => {
    const result = await OrderApi.getMyOrderedPublications();
    if (result) setOrderedPublications(result);
  };

  const setMine = () => {
    const mine = publications.filter((publication) => {
      return orderedPublications?.map((oi) => oi.id).includes(publication.id);
    });
    setMyPublications(mine);
  };

  useEffect(() => {
    if (publications?.length > 0) {
      setMine();
    }
  }, [publications, orderedPublications]);

  useEffect(() => {
    getPublications();
    getMyPublications();
    if (flipbookOpened && selectedPdf) {
      initializeFlipBook(selectedPdf);
    }
    return () => {
      if (flipbookContainer) {
        flipbookContainer.remove();
        flipbookContainer = null;
      }
    };
  }, [flipbookOpened, selectedPdf]);

  const initializeFlipBook = (pdfUrl: string) => {
    flipbookContainer = document.createElement("div");
    flipbookContainer.id = "flipbook-container";
    document.body.appendChild(flipbookContainer);

    //@ts-ignore
    $(flipbookContainer).flipBook({
      pdfUrl,
      lightBox: true,
      layout: 3,
      currentPage: { vAlign: "bottom", hAlign: "left" },
      btnPrint: { enabled: false },
      btnDownloadPages: {
        enabled: false,
      },
      btnDownloadPdf: { enabled: false },
      btnColor: "#00509f",
      sideBtnColor: "#00509f",
      sideBtnSize: 60,
      sideBtnBackground: "rgba(0,0,0,.7)",
      sideBtnRadius: 60,
      btnSound: { vAlign: "top", hAlign: "left" },
      btnAutoplay: { vAlign: "top", hAlign: "left" },
      btnShare: {
        enabled: false,
      },
    });

    setTimeout(() => {
      flipbookContainer?.click();
    }, 100);

    flipbookContainer.addEventListener("click", (event) => {
      if (event.target === flipbookContainer) {
        closeFlipbook();
      }
    });
  };

  const closeFlipbook = () => {
    if (flipbookContainer) {
      flipbookContainer.remove();
      flipbookContainer = null;
    }
    setFlipbookOpened(false);
    setSelectedPdf("");
  };

  return (
    <div className="publication-user-container">
      <div className="publication-user-header">
        <div className="text-[#004A99] text-xl font-bold">Publikacije</div>
        <Button.Group className="button-nav">
          <Button
            value="single"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[40px] !w-[50px] !p-0",
              isSingleView ? "!bg-white" : "view-button"
            )}
            onClick={() => setView("single")}
          >
            <SingleViewIcon
              width={20}
              height={20}
              className={isSingleView ? "text-[#d5c591]" : "text-white"}
            />
          </Button>
          <div className="flex items-center">
            <div className="w-0.5 h-4 bg-white rounded-sm" />
          </div>
          <Button
            value="list"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[40px] !w-[50px] !p-0",
              isListView ? "!bg-white" : "view-button"
            )}
            onClick={() => setView("list")}
          >
            <ListViewIcon
              width={20}
              height={20}
              className={isListView ? "text-[#d5c591]" : "text-white"}
            />
          </Button>
          <div className="flex items-center">
            <div className="w-0.5 h-4 bg-white rounded-sm" />
          </div>
          <Button
            value="grid"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[40px] !w-[50px] !p-0",
              isGridView ? "!bg-white" : "view-button"
            )}
            onClick={() => setView("grid")}
          >
            <GridViewIcon
              width={20}
              height={20}
              className={isGridView ? "text-[#d5c591]" : "text-white"}
            />
          </Button>
        </Button.Group>
      </div>
      <div className="publication-user-mobile-header">
        <div className="flex gap-3 items-center">
          <Logo width={24} height={24} />
          <div className="text-[#004a99] text-lg font-bold">Publikacije</div>
        </div>
      </div>
      {!publications || !publications.length ? (
        <div className="empty-publications-div">
          <span>Trenutno nema publikacija.</span>
        </div>
      ) : (
        <div className="publication-views">
          {isSingleView && (
            <SingleView
              type="publication"
              items={publications}
              myItems={myPublications}
              openFlipbook={openFlipbook}
            ></SingleView>
          )}
          {isListView && (
            <ListView
              type="publication"
              items={publications}
              myItems={myPublications}
              openFlipbook={openFlipbook}
            ></ListView>
          )}
          {isGridView && (
            <GridView
              type="publication"
              items={publications}
              myItems={myPublications}
              openFlipbook={openFlipbook}
            ></GridView>
          )}
        </div>
      )}
      <div className="publication-user-mobile-footer !z-50">
        {isGridView && (
          <Button
            value="single"
            variant="filled"
            className={clsx("!rounded-xl !h-[55px] !w-[55px]", "view-button")}
            onClick={() => setView("single")}
          >
            <SingleViewIcon width={20} height={20} className={"text-white"} />
          </Button>
        )}
        {isSingleView && (
          <Button
            value="list"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[55px] !w-[55px] !z-50",
              "view-button"
            )}
            onClick={() => setView("list")}
          >
            <ListViewIcon width={20} height={20} className={"text-white"} />
          </Button>
        )}
        {isListView && (
          <Button
            value="grid"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[55px] !w-[55px] !p-0",
              "view-button"
            )}
            onClick={() => setView("grid")}
          >
            <GridViewIcon width={20} height={20} className={"text-white"} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default Publications;
